import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, BookOpen, ArrowRight } from "lucide-react";

/**
 * BlogCarousel — orizzontale, scroll-snap, swipe mobile, frecce desktop.
 *
 * Mostra le ultime N guide/articoli pubblicati. Pensato per stare appena
 * sotto la hero in homepage. Sketchy progressive enhancement: il body è
 * sempre renderizzato (anche in SSR), le frecce compaiono solo se necessario.
 */
export default function BlogCarousel({ articles, max = 4 }) {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const items = (articles || []).slice(0, max);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return undefined;
    const onScroll = () => updateArrows();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateArrows);
    };
  }, [items.length]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.max(280, Math.round(el.clientWidth * 0.85));
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!items.length) return null;

  return (
    <section
      data-testid="blog-carousel"
      className="border-y border-zinc-900 bg-gradient-to-b from-zinc-950/60 to-transparent"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-6">
          <div>
            <div className="font-mono-bdd text-xs uppercase tracking-[0.3em] text-[#FBD51C] mb-2">
              Dal blog
            </div>
            <h2 className="font-display font-black text-2xl sm:text-3xl text-white tracking-tight">
              Ultimi dal blog
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/guide"
              data-testid="blog-carousel-view-all"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-[#FBD51C] hover:underline"
            >
              Vedi tutti <ArrowRight size={14} />
            </Link>
            <div className="hidden md:flex items-center gap-2 ml-2">
              <button
                type="button"
                aria-label="Scorri a sinistra"
                data-testid="blog-carousel-prev"
                onClick={() => scrollBy(-1)}
                disabled={!canPrev}
                className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                aria-label="Scorri a destra"
                data-testid="blog-carousel-next"
                onClick={() => scrollBy(1)}
                disabled={!canNext}
                className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin scrollbar-thumb-zinc-800"
          style={{ scrollbarWidth: "thin" }}
        >
          {items.map((a) => (
            <Link
              key={a.id || a.slug}
              to={`/guide/${a.slug}`}
              data-testid={`blog-carousel-card-${a.slug}`}
              className="shrink-0 w-[280px] sm:w-[320px] snap-start group bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-[#FBD51C]/40 rounded-2xl overflow-hidden transition-all flex flex-col"
            >
              {a.cover_image ? (
                <img
                  src={a.cover_image}
                  alt={a.title}
                  loading="lazy"
                  className="w-full aspect-[16/9] object-cover bg-zinc-950"
                />
              ) : (
                <div className="w-full aspect-[16/9] bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center text-zinc-700">
                  <BookOpen size={28} />
                </div>
              )}
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-center gap-2 text-[11px] text-zinc-500 mb-2">
                  <BookOpen size={11} />
                  <span>Guida</span>
                  <span className="text-zinc-700">·</span>
                  <span>
                    {a.created_at
                      ? new Date(a.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })
                      : ""}
                  </span>
                </div>
                <h3 className="font-display text-white text-base sm:text-lg leading-snug mb-2 line-clamp-2">
                  {a.title}
                </h3>
                {a.excerpt && (
                  <p className="text-zinc-400 text-sm leading-relaxed line-clamp-2 mb-3">
                    {a.excerpt}
                  </p>
                )}
                <span className="mt-auto inline-flex items-center gap-1 text-sm text-[#FBD51C] font-bold opacity-80 group-hover:opacity-100">
                  Leggi <ArrowRight size={13} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
